import { useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { event } from '@hotjar/webapp/tracking/tools/hotjar';
import { sendHighlightEvent } from '@hotjar/webapp/domains/highlights/api';
import { HighlightsEventType } from '@hotjar/webapp/domains/highlights/dto';
import type { UIPosition } from '@hotjar/webapp/domains/highlights/api';
import type { SelectedArea } from '@hotjar/webapp/domains/highlights/heatmap/model';

import { HeatmapImage } from '../Heatmaps/HeatmapImage';
import { useDownloadHeatmapHighlight } from './use-download-heatmap-highlight';

const HEATMAP_HIGHLIGHT_DOWNLOADED = 'heatmap_highlight_downloaded';

function track(siteId: string, highlightId: string, uiPosition?: UIPosition) {
  sendHighlightEvent(
    siteId,
    { highlightId, eventType: HighlightsEventType.HIGHLIGHT_DOWNLOADED },
    { uiPosition },
  );

  event(HEATMAP_HIGHLIGHT_DOWNLOADED);
}

const StyledHeatmapImage = styled(HeatmapImage)`
  position: fixed;
  top: 0;
  left: 0;
  pointer-events: none;
  opacity: 0;
`;

export const HeatmapHighlightDownload = ({
  highlightId,
  imageUrl,
  selectedArea,
  afterDownload,
  siteId,
  trackingUiPosition,
}: {
  highlightId: string;
  imageUrl: string;
  selectedArea: SelectedArea;
  afterDownload: () => void;
  siteId: string;
  trackingUiPosition: UIPosition;
}): JSX.Element | null => {
  const [isDownloaded, setIsDownloaded] = useState(false);
  const downloadHighlight = useDownloadHeatmapHighlight(siteId, highlightId);

  useEffect(() => {
    if (!imageUrl) {
      return;
    }

    track(siteId, highlightId, trackingUiPosition);
  }, [imageUrl, siteId, highlightId, trackingUiPosition]);

  if (!imageUrl) {
    return null;
  }

  return (
    <StyledHeatmapImage
      onReady={(ref) => {
        if (ref && !isDownloaded) {
          setIsDownloaded(true);
          downloadHighlight(ref).finally(() => {
            afterDownload();
          });
        }
      }}
      url={imageUrl}
      clicksCount={0}
      clicksPercentage={0}
      isDesktop
      selectedArea={selectedArea}
    />
  );
};
